import React, {createRef, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {isEmpty} from 'lodash';
import config from '../../../config.js';
import UserContext from '../../common/UserContext.js';

// css
import '../../../assets/css/FSADashboard.css';

// reactstrap
import {Col, Row} from 'reactstrap';

// components
import BudgetUtilizationChart  from './BudgetUtilizationChart.js';
import HostUtilizationChart from './HostUtilizationChart.js';
import ProductUtilizationChart from './ProductUtilizationChart.js';
import {useSupportContext} from "../../common/SupportContext";

// kendo react
import {Card, CardTitle, CardBody} from '@progress/kendo-react-layout';
import {DatePicker} from '@progress/kendo-react-dateinputs';
import {Button} from '@progress/kendo-react-buttons';
import {Tooltip} from '@progress/kendo-react-tooltip';
import {ExcelExport, ExcelExportColumn} from '@progress/kendo-react-excel-export';
import {fileExcelIcon} from '@progress/kendo-svg-icons';

// multilingual
import {Text} from '../../common/MultilingualText.js';
import {useLocalization} from '@progress/kendo-react-intl';
import {
    remainingBalanceKey,
    noDataAvailableKey,
    totalCostKey,
    balanceOverTimeKey,
    budgetConsumptionByDateKey,
    exportAllExcelKey,
    budgetUtilizationDetailsByHostKey,
    top10ProductsSpentKey,
    fSAIDKey,
    customerNameKey,
    yearKey,
    termStartDate,
    termEndDate,
    extendedBudgetExtendedBudgetDefKey,
    utilizedBudgetKey,
    percentageUsedKey,
    productNumberKey,
    productDescriptionKey,
    priceKey,
    qtyKey,
    hostKey,
    startDateKey,
    endDateKey,
    licenseStartDateKey,
    licenseEndDateKey,
    licenseTypeKey,
    dataRequestorInformationKey,
    mainMessages
} from '../../../assets/text/MultilingualText.js';

const chartColors = ["#30578D", "#F2A945", "#468657"];

function FSADashboard(props) {
    const {
        eA,
        setEA,
        users,
        setUsers,
        setYears,
        setFilter,
        setAlerts,
        setIsLoading,
        INFO_ALERT,
        ALL_USERS_KEY
    } = props;
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();

    const [periods, setPeriods] = useState([]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [dateRange, setDateRange] = useState({
        start: today,
        end: today,
        max: today,
        min: today
    });

    const [subscriptionPool, setSubscriptionPool] = useState([]);
    // filtered subscription pool
    const [pool, setPool] = useState([]);

    const [currency, setCurrency] = useState("");

    // kendo react excel export - budget overview
    const [budgetOverviewExcelData, setBudgetOverviewExcelData] = useState([]);
    const [currentYearProductsExcelData, setCurrentYearProductsExcelData] = useState([]);
    const _budgetOverviewExporter = createRef();
    const _currentYearProductsExporter = createRef();
    const exportBudgetOverviewExcel = () => {
        let budgetOverviewSheet = _budgetOverviewExporter.current.workbookOptions();
        // fsa id and customer name table
        const rows = budgetOverviewSheet.sheets[0].rows;
        let style = {
            background: "#7a7a7a",
            color: "#fff"
        };
        let headerCells = [
            {
                ...style,
                value: localization.toLanguageString(fSAIDKey, mainMessages[siteLanguageDefault][fSAIDKey])
            },
            {
                ...style,
                value: localization.toLanguageString(customerNameKey, mainMessages[siteLanguageDefault][customerNameKey])
            },
        ];
        let dataCells = [
            {value: eA?.name?.id},
            {value: eA?.name?.agreement},
        ]
        let header = [
            {
                type: 'header',
                cells: headerCells
            },
            {
                type: 'data',
                cells: dataCells
            },
            {}
        ]
        rows.unshift(...header);

        let currentYearProductsSheet = _currentYearProductsExporter.current.workbookOptions();
        budgetOverviewSheet.sheets[0].name = 'Budget Utilization';
        currentYearProductsSheet.sheets[0].name = 'Year ' + eA.year + ' Products';

        budgetOverviewSheet.sheets = budgetOverviewSheet.sheets
            .concat(currentYearProductsSheet.sheets)

        _budgetOverviewExporter.current.save(budgetOverviewSheet);
    };

    // kendo react excel export - budget period
    const [budgetPeriodExcelData, setBudgetPeriodExcelData] = useState([]);
    const [budgetPeriodExcelFileName, setBudgetPeriodExcelFileName] = useState("");
    const [disableExcelExport, setDisableExcelExport] = useState({
        overview: true,
        period: true
    });
    const _budgetPeriodExporter = createRef();
    const exportBudgetPeriodExcel = () => {
        let budgetPeriodSheet = _budgetPeriodExporter.current.workbookOptions();
        // fsa id and customer name table
        const rows = budgetPeriodSheet.sheets[0].rows;
        let style = {
            background: "#7a7a7a",
            color: "#fff"
        };
        let headerCells = [
            {
                ...style,
                value: localization.toLanguageString(fSAIDKey, mainMessages[siteLanguageDefault][fSAIDKey])
            },
            {
                ...style,
                value: localization.toLanguageString(customerNameKey, mainMessages[siteLanguageDefault][customerNameKey])
            },
        ];
        let dataCells = [
            {value: eA?.name?.id},
            {value: eA?.name?.agreement},
        ]
        let header = [
            {
                type: 'header',
                cells: headerCells
            },
            {
                type: 'data',
                cells: dataCells
            },
            {}
        ]
        rows.unshift(...header);

        _budgetPeriodExporter.current.save(budgetPeriodSheet);
    };

    const AXIS_LABELS = {
        font: '14px Graphik LG Web, serif',
        rotation: 'auto'
    };

    // no data overlay
    const NoDataOverlay = (props) => {
        let style = {
            ...props.style,
            alignContent: "center",
            textAlign: "center"
        };
        return (
            <div style={style}>
                {localization.toLanguageString(noDataAvailableKey, mainMessages[siteLanguageDefault][noDataAvailableKey])}
            </div>
        )
    }

    /*
     * onDateRangeChange() updates the date range object on change
     * @param {e} the input change event
    */
    const onDateRangeChange = (e) => {
        let field = e.target.props.field;
        setDateRange(dateRange => ({
            ...dateRange,
            [field]: e.value
        }));
    }

    /*
     * getFSABudget() gets all FSA budget data
    */
    function getFSABudget() {
        let headers = {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + accessToken
        };

        setIsLoading(true);

        axios.get(
            config.fsa_dashboard.FSA,
            {
                headers: headers,
                params: {id: eA.name.id}
            }
        )
            .then((response) => {
                if (response.status === 200) {
                    let data = response.data || [];
                    let periods = [];
                    let previous_total = 0;
                    let subscriptionPool = data
                        .map(d => {
                            let fsAID = eA.name.id;
                            let startDate = d.period_start_date && moment(d.period_start_date).isValid() ? new Date(d.period_start_date) : null;
                            let endDate = d.period_end_date && moment(d.period_end_date).isValid() ? new Date(d.period_end_date) : null;
                            let consumed = d.total_consumed;
                            let percent = consumed / d.total_budget;
                            let products = d.values
                                .map(p => ({
                                    ...p,
                                    fsa_id: fsAID,
                                    start_date: new Date(p.license_start_date),
                                    end_date: new Date(p.license_end_date),
                                    ea_period_id: d.fsa_year,
                                    period_start_date: startDate,
                                    period_end_date: endDate
                                }))
                                .sort((a, b) => a - b);
                            let period = {
                                period: d.fsa_year,
                                period_date: "Period " + d.fsa_year + " (" + moment(startDate).format('ll') + " - " + moment(endDate).format('ll') + ")"
                            };
                            products.forEach((product, index) => {
                                previous_total = previous_total + product.total_cost;
                            });
                            periods.push(period);

                            return {
                                ...period,
                                year: d.fsa_year,
                                start_date: startDate,
                                end_date: endDate,
                                consumed: consumed,
                                total: d.fsa_year == 1 ? d.total_budget : d.total_budget - previous_total,
                                percent: percent,
                                currency: d.currency_code,
                                products: products
                            }
                        })
                        .sort((a, b) => a.year - b.year)
                        .sort((a, b) => a.period - b.period);

                    periods.sort((a, b) => a.period - b.period);

                    setSubscriptionPool(subscriptionPool);
                    setPool(subscriptionPool);
                    setPeriods(periods);
                    setCurrency(subscriptionPool[0]?.currency);
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to GET FSA Budget", error);

                setSubscriptionPool([]);
                setPool([]);
                setPeriods([]);

                // generic error message
                let alert = {
                    type: 'error',
                    title: localization.toLanguageString(noDataAvailableKey, mainMessages[siteLanguageDefault][noDataAvailableKey])
                };
                setAlerts(alerts => [...alerts, alert]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    // 2. on load - get budget utilization on fsa name change
    useEffect(() => {
        if (eA.name.id && eA.name.type === "FSA") {
            getFSABudget();
        }
    }, [eA.name]);

    // 3. on load - set default users, years, current year, current period, and date range (variable) on subscription pool change
    useEffect(() => {
        setAlerts([]);
        if (subscriptionPool.length && periods.length) {
            const today = new Date();
            let currentPool = subscriptionPool.find((pool) => {
                const startDate = new Date(pool.start_date);
                const endDate = new Date(pool.end_date);
                return today >= startDate && today <= endDate;
            });

            // handle no current pool
            if (!currentPool) {
                currentPool = subscriptionPool.reduce((max, pool) => pool.period > max.period ? pool : max, subscriptionPool[0]);
            }

            // set users
            let productUsers = currentPool.products
                .filter(p => p.requested_by)
                .map(p => p.requested_by);
            let allUsers = [...new Set([ALL_USERS_KEY, ...productUsers])];
            setFilter(allUsers.slice());
            setUsers(allUsers);

            // set years
            setYears([...new Set(subscriptionPool.map(p => p.year))].sort());

            // set all users (default), current year, and current period
            setEA(eA => ({
                ...eA,
                user: allUsers,
                year: currentPool.year,
                period: periods.find(p => p.period === currentPool.period)
            }));

            // set date range
            setDateRange(dateRange => ({
                ...dateRange,
                start: currentPool.start_date,
                end: currentPool.end_date,
                min: currentPool.start_date,
                max: currentPool.end_date
            }));
        }
    }, [subscriptionPool, periods]);

    // user - filter pool data, users, and date range (variable) dropdown on year change
    useEffect(() => {
        setAlerts([]);
        let yearPool = subscriptionPool.filter(p => p.year === eA.year);
        if (yearPool.length) {
            // filter subscription pool by year
            setPool(yearPool);

            // set users by year
            let poolProducts = [].concat(...yearPool.map(p => p.products));
            let productUsers = poolProducts
                .filter(p => p.requested_by)
                .map(p => p.requested_by);
            let allUsers = [...new Set([ALL_USERS_KEY, ...productUsers])];
            setFilter(allUsers.slice());
            setUsers(allUsers);
            setEA(eA => ({
                ...eA,
                user: allUsers
            }));

            // set date range by year and period
            let pool = subscriptionPool.find(p => p.year === eA.year);
            setDateRange(dateRange => ({
                ...dateRange,
                start: pool.start_date,
                end: pool.end_date,
                min: pool.start_date,
                max: pool.end_date
            }));

            setEA(eA => ({
                ...eA,
                period: periods.find(p => p.period === pool.period)
            }));
        }
    }, [eA.year]);

    // user - update products and consumed subscription pool on user selection
    // note: filters on current year
    useEffect(() => {
        let yearPool = subscriptionPool.filter(p => p.year === eA.year);
        // case: all users
        if (eA.user.includes(ALL_USERS_KEY)) {
            setPool(yearPool);
        }
        // case: selected users
        else if (eA.user.length && !eA.user.includes(ALL_USERS_KEY)) {
            const updates = yearPool.map(p => {
                const filtered = p.products.filter(p => eA.user.includes(p.requested_by));
                const consumed = filtered.reduce((accumulator, product) => accumulator + product.total_cost, 0);
                const percent = consumed / p.total;
                return {
                    ...p,
                    products: filtered,
                    consumed: consumed,
                    percent: percent
                }
            });

            // show error for no data on selected users (data available after next period)
            if (!updates.length) {
                let alert = {
                    type: 'error',
                    title: localization.toLanguageString(noDataAvailableKey, mainMessages[siteLanguageDefault][noDataAvailableKey]),
                    message: localization.toLanguageString(dataRequestorInformationKey, mainMessages[siteLanguageDefault][dataRequestorInformationKey])
                };
                setAlerts(alerts => [...alerts, alert]);
            }

            setPool(updates);
        }
    }, [eA.user]);

    // set info alert when no FSA is selected
    useEffect(() => {
        !eA?.name?.name ? setAlerts([INFO_ALERT]) : setAlerts([]);
    }, [eA.name]);

    // set to all users by default and on clear
    useEffect(() => {
        if (eA.user.length === 0 && users.length) {
            setEA(eA => ({...eA, user: users}));
        }
    }, [eA.user]);

    // excel export - budget overview
    useEffect(() => {
        if (eA.year && eA.period) {
            let budgetOverview = [];
            let currentYearProducts = [];

            // set excel data for budget overview
            budgetOverview = subscriptionPool.map(pool => ({
                period_start_date: pool.start_date,
                period_end_date: pool.end_date,
                fsa_year: pool.year,
                total_budget: pool.total,
                total_consumed: pool.consumed,
                percent: pool.percent * 100
            }));
            setBudgetOverviewExcelData(budgetOverview);

            // set excel data for products in current year
            const CURRENT_YEAR_POOL = subscriptionPool.filter(p => p.year === eA.year);
            CURRENT_YEAR_POOL.forEach(pool => {
                pool.products.forEach(product => {
                    if (product.product_id) {
                        currentYearProducts = [...currentYearProducts, product];
                    }
                });
            });
            setCurrentYearProductsExcelData(currentYearProducts);

            // enable excel export
            setDisableExcelExport(disableExcelExport => ({
                ...disableExcelExport,
                overview: false
            }));
        }
    }, [eA.year, eA.period]);

    // excel export - budget period
    useEffect(() => {
        let pool = [];
        let subscriptionPoolProducts = [];

        // get pool based on year and filter products on date range for fsa's
        if (!isEmpty(subscriptionPool)) {
            pool = subscriptionPool.find(p => p.year === eA.year);
            subscriptionPoolProducts = pool.products.filter(p => new Date(p.license_start_date) >= dateRange.start && new Date(p.license_end_date) <= dateRange.end) || [];

            // set excel file name
            let startDate = moment(dateRange.start).format('MM_DD_YYYY');
            let endDate = moment(dateRange.end).format('MM_DD_YYYY');
            setBudgetPeriodExcelFileName("FSA_BUDGET_UTILIZATION_YEAR_" + eA.year + "_" + startDate + "_" + endDate + ".xlsx");
        }

        // set excel data
        setBudgetPeriodExcelData(subscriptionPoolProducts);
        subscriptionPoolProducts.length ? setDisableExcelExport(disableExcelExport => ({
            ...disableExcelExport,
            period: false
        })) : setDisableExcelExport(disableExcelExport => ({
            ...disableExcelExport,
            period: true
        }));
    }, [eA.period, dateRange]);

    useEffect(() => {
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {fsa_id: eA.id, ea_name: eA.name} });
    }, [eA]);

    // useEffect(() => {
    //     console.log("users", users);
    // }, [users]);
    //
    // useEffect(() => {
    //     console.log("subscription pool", subscriptionPool);
    // }, [subscriptionPool]);
    //
    // useEffect(() => {
    //     console.log("pool", pool);
    // }, [pool]);

    // useEffect(() => {
    //     let range = {}
    //     Object.keys(dateRange).forEach(key => {
    //         range[key] = moment(dateRange[key]).format('ll');
    //     });
    //     console.log("dateRange", range);
    // }, [dateRange]);

    // Support Context Metadata
    // Set Module and Step values on page load
    useEffect(() => {
        dispatch({ type: 'MODULE_LOAD', payload: {module: 'FSA Dashboard', step: ''}});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Row className={"mt-4 mb-2"}>
                <Col className={"d-flex align-items-center"}>
                    <div className={"k-h5 mb-0"}>
                        {localization.toLanguageString(remainingBalanceKey, mainMessages[siteLanguageDefault][remainingBalanceKey])}
                    </div>
                </Col>
                <Col className={"d-flex align-items-end justify-content-end"}>
                    <Tooltip
                        anchorElement="target"
                        showCallout={true}
                        parentTitle={true}
                        openDelay={0}
                        position="left"
                    >
                        <Button
                            title={localization.toLanguageString(exportAllExcelKey, mainMessages[siteLanguageDefault][exportAllExcelKey])}
                            themeColor={"tertiary"}
                            fillMode={"solid"}
                            svgIcon={fileExcelIcon}
                            disabled={disableExcelExport.overview}
                            onClick={exportBudgetOverviewExcel}
                        />
                    </Tooltip>
                    <ExcelExport
                        data={budgetOverviewExcelData}
                        ref={_budgetOverviewExporter}
                        fileName={"FSA_BUDGET_UTILIZATION.xlsx"}
                        onExportComplete={() => setIsLoading(false)}
                    >
                        <ExcelExportColumn
                            field="fsa_year"
                            title={localization.toLanguageString(yearKey, mainMessages[siteLanguageDefault][yearKey])}
                        />
                        <ExcelExportColumn
                            field="period_start_date"
                            title={localization.toLanguageString(termStartDate, mainMessages[siteLanguageDefault][termStartDate])}
                        />
                        <ExcelExportColumn
                            field="period_end_date"
                            title={localization.toLanguageString(termEndDate, mainMessages[siteLanguageDefault][termEndDate])}
                        />
                        <ExcelExportColumn
                            field="total_budget"
                            title={localization.toLanguageString(extendedBudgetExtendedBudgetDefKey, mainMessages[siteLanguageDefault][extendedBudgetExtendedBudgetDefKey])}
                        />
                        <ExcelExportColumn
                            field="total_consumed"
                            title={localization.toLanguageString(utilizedBudgetKey, mainMessages[siteLanguageDefault][utilizedBudgetKey])}
                        />
                        <ExcelExportColumn
                            field="percent"
                            title={localization.toLanguageString(percentageUsedKey, mainMessages[siteLanguageDefault][percentageUsedKey])}
                        />
                    </ExcelExport>
                    <ExcelExport
                        data={currentYearProductsExcelData}
                        ref={_currentYearProductsExporter}
                        fileName={"FSA_BUDGET_UTILIZATION.xlsx"}
                    >
                        <ExcelExportColumn
                            field="period_start_date"
                            title={localization.toLanguageString(termStartDate, mainMessages[siteLanguageDefault][termStartDate])}
                        />
                        <ExcelExportColumn
                            field="period_end_date"
                            title={localization.toLanguageString(termEndDate, mainMessages[siteLanguageDefault][termEndDate])}
                        />
                        <ExcelExportColumn
                            field="fsa_id"
                            title={localization.toLanguageString(fSAIDKey, mainMessages[siteLanguageDefault][fSAIDKey])}
                        />
                        <ExcelExportColumn
                            field="prod_num"
                            title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                        />
                        <ExcelExportColumn
                            field="product_description"
                            title={localization.toLanguageString(productDescriptionKey, mainMessages[siteLanguageDefault][productDescriptionKey])}
                        />
                        <ExcelExportColumn
                            field="license_type"
                            title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                        />
                        <ExcelExportColumn
                            field="price"
                            title={localization.toLanguageString(priceKey, mainMessages[siteLanguageDefault][priceKey])}
                        />
                        <ExcelExportColumn
                            field="quantity"
                            title={localization.toLanguageString(qtyKey, mainMessages[siteLanguageDefault][qtyKey])}
                        />
                        <ExcelExportColumn
                            field="total_cost"
                            title={localization.toLanguageString(totalCostKey, mainMessages[siteLanguageDefault][totalCostKey])}
                        />
                        <ExcelExportColumn
                            field="host_id"
                            title={localization.toLanguageString(hostKey, mainMessages[siteLanguageDefault][hostKey])}
                        />
                        <ExcelExportColumn
                            field="start_date"
                            title={localization.toLanguageString(licenseStartDateKey, mainMessages[siteLanguageDefault][licenseStartDateKey])}
                        />
                        <ExcelExportColumn
                            field="end_date"
                            title={localization.toLanguageString(licenseEndDateKey, mainMessages[siteLanguageDefault][licenseEndDateKey])}
                        />
                    </ExcelExport>
                </Col>
            </Row>
            <Row className={"my-2"}>
                <Col>
                    <Card className={"fsa-dashboard-card-border"}>
                        <CardBody>
                            <CardTitle className={"fsa-dashboard-card-title"}>
                                {localization.toLanguageString(balanceOverTimeKey, mainMessages[siteLanguageDefault][balanceOverTimeKey])}
                            </CardTitle>
                            <div className={"fsa-dashboard-chart-margin"}>
                                {eA.name ? <BudgetUtilizationChart
                                    eA={eA}
                                    pool={pool}
                                    currency={currency}
                                    chartColors={chartColors}
                                    AXIS_LABELS={AXIS_LABELS}
                                    NoDataOverlay={NoDataOverlay}
                                /> : <NoDataOverlay style={{height: '50vh'}}/>}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row className={"mt-4 mb-2"}>
                <Col className={"d-flex align-items-center"}>
                    <div className={"k-h5 mb-0"}>
                        {localization.toLanguageString(budgetConsumptionByDateKey, mainMessages[siteLanguageDefault][budgetConsumptionByDateKey])}
                    </div>
                </Col>
            </Row>
            <Row className={"my-2"}>
                <Col
                    xs={4}
                    className={"d-flex align-items-end"}
                >
                    <Row>
                        <Col
                            xs={6}
                            className={"pe-1"}
                        >
                            <Text
                                textkey={startDateKey}
                                textdefault={mainMessages[siteLanguageDefault][startDateKey]}
                            />
                            <DatePicker
                                value={dateRange.start}
                                format="d-MMM-y"
                                field={"start"}
                                max={dateRange.max}
                                min={dateRange.min}
                                onChange={onDateRangeChange}
                            />
                        </Col>
                        <Col
                            xs={6}
                            className={"ps-1"}
                        >
                            <Text
                                textkey={endDateKey}
                                textdefault={mainMessages[siteLanguageDefault][endDateKey]}
                            />
                            <DatePicker
                                value={dateRange.end}
                                format="d-MMM-y"
                                field={"end"}
                                max={dateRange.max}
                                min={dateRange.min}
                                onChange={onDateRangeChange}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col
                    xs={8}
                    className={"d-flex align-items-end justify-content-end"}
                >
                    <Tooltip
                        anchorElement="target"
                        showCallout={true}
                        parentTitle={true}
                        openDelay={0}
                        position="left"
                    >
                        <Button
                            title={localization.toLanguageString(exportAllExcelKey, mainMessages[siteLanguageDefault][exportAllExcelKey])}
                            themeColor={"tertiary"}
                            fillMode={"solid"}
                            svgIcon={fileExcelIcon}
                            disabled={disableExcelExport.period}
                            onClick={exportBudgetPeriodExcel}
                        />
                    </Tooltip>
                    <ExcelExport
                        data={budgetPeriodExcelData}
                        fileName={budgetPeriodExcelFileName}
                        ref={_budgetPeriodExporter}
                        onExportComplete={() => setIsLoading(false)}
                    >
                        <ExcelExportColumn
                            field="prod_num"
                            title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                        />
                        <ExcelExportColumn
                            field="product_description"
                            title={localization.toLanguageString(productDescriptionKey, mainMessages[siteLanguageDefault][productDescriptionKey])}
                        />
                        <ExcelExportColumn
                            field="license_type"
                            title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                        />
                        <ExcelExportColumn
                            field="price"
                            title={localization.toLanguageString(priceKey, mainMessages[siteLanguageDefault][priceKey])}
                        />
                        <ExcelExportColumn
                            field="quantity"
                            title={localization.toLanguageString(qtyKey, mainMessages[siteLanguageDefault][qtyKey])}
                        />
                        <ExcelExportColumn
                            field="total_cost"
                            title={localization.toLanguageString(totalCostKey, mainMessages[siteLanguageDefault][totalCostKey])}
                        />
                        <ExcelExportColumn
                            field="host_id"
                            title={localization.toLanguageString(hostKey, mainMessages[siteLanguageDefault][hostKey])}
                        />
                        <ExcelExportColumn
                            field="start_date"
                            title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                        />
                        <ExcelExportColumn
                            field="end_date"
                            title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                        />
                    </ExcelExport>
                </Col>
            </Row>
            <Row className={"my-2"}>
                <Col>
                    <Card className={"fsa-dashboard-card-border"}>
                        <CardBody>
                            <CardTitle>
                                <Row>
                                    <Col className={"fsa-dashboard-card-title"}>
                                        {localization.toLanguageString(budgetUtilizationDetailsByHostKey, mainMessages[siteLanguageDefault][budgetUtilizationDetailsByHostKey])}
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <HostUtilizationChart
                                            eA={eA}
                                            subscriptionPool={subscriptionPool}
                                            currency={currency}
                                            chartColors={chartColors}
                                            dateRange={dateRange}
                                            ALL_USERS_KEY={ALL_USERS_KEY}
                                            AXIS_LABELS={AXIS_LABELS}
                                            NoDataOverlay={NoDataOverlay}
                                        />
                                    </Col>
                                </Row>
                            </CardTitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card className={"fsa-dashboard-card-border"}>
                        <CardBody>
                            <CardTitle>
                                <Row>
                                    <Col className={"fsa-dashboard-card-title"}>
                                        {localization.toLanguageString(top10ProductsSpentKey, mainMessages[siteLanguageDefault][top10ProductsSpentKey])}
                                    </Col>
                                </Row>
                            </CardTitle>
                            <Row>
                                <Col>
                                    <ProductUtilizationChart
                                        eA={eA}
                                        subscriptionPool={subscriptionPool}
                                        currency={currency}
                                        chartColors={chartColors}
                                        dateRange={dateRange}
                                        ALL_USERS_KEY={ALL_USERS_KEY}
                                        AXIS_LABELS={AXIS_LABELS}
                                        NoDataOverlay={NoDataOverlay}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default FSADashboard;